import type { LogCategories, LogSubCategories, LogTags } from "./logger-types";
import type { AuthMode } from "@/context/auth-provider";

export type LogLevel = "INFO" | "WARN" | "ERROR" | "DEBUG";
const DEFAULT_MAX_BUFFER_SIZE = 20;
const DEFAULT_FLUSH_INTERVAL_MS = 60_000;

export type LogEventType = "PAGE_VIEW" | "USER_INTERACTION";
// | "TRACK"
// | "APP_START"
// | "API_SUCCESS"
// | "API_ERROR"
// | "ERROR"
// | "CRASH";

export type BaseLogEvent = {
  category: LogCategories;
  context: Record<string, unknown>;
  tag: LogTags;
  title: string;
  subCategory: LogSubCategories;
  type: LogEventType;
  duration?: number;
  mode: AuthMode;
  module: LogSubCategories | LogCategories;
};

// --------- Logger FSM ---------

export type LoggerState = "IDLE" | "BUFFERING" | "FLUSHING" | "ERROR";

export type LoggerEvent =
  | { type: "ADD_LOG"; payload: BaseLogEvent }
  | { type: "FLUSH" }
  | { type: "SUCCESS" }
  | { type: "FAIL" };

type FSMContext = {
  buffer: BaseLogEvent[];
};

class LoggerFSM {
  private state: LoggerState = "IDLE";
  private context: FSMContext = { buffer: [] };
  private inFlight: BaseLogEvent[] = [];
  private dropCurrentFlushOutcome = false;

  constructor(
    private flushHandler: (logs: BaseLogEvent[]) => Promise<void>,
    private maxBufferSize: number,
  ) {}

  getState() {
    return this.state;
  }

  /**
   * Returns all pending logs (buffer + in-flight) and clears them.
   * Used on unload to persist without waiting for async flush completion.
   */
  getPendingAndClear(): BaseLogEvent[] {
    const copy = [...this.inFlight, ...this.context.buffer];
    if (this.inFlight.length > 0) {
      // If a flush is currently in progress, ignore its eventual success/fail
      // because we already moved that payload to durable storage.
      this.dropCurrentFlushOutcome = true;
    }
    this.inFlight = [];
    this.context.buffer = [];
    this.state = "IDLE";
    return copy;
  }

  getBufferSize() {
    return this.context.buffer.length;
  }

  dispatch(event: LoggerEvent) {
    switch (this.state) {
      case "IDLE":
        if (event.type === "ADD_LOG") {
          this.context.buffer.push(event.payload);
          this.state = "BUFFERING";
        }
        if (event.type === "FLUSH") {
          void this.flush();
        }
        break;
      case "BUFFERING":
        if (event.type === "ADD_LOG") {
          this.context.buffer.push(event.payload);
        }

        if (event.type === "FLUSH") {
          void this.flush();
        }
        break;
      case "FLUSHING":
        if (event.type === "ADD_LOG") {
          this.context.buffer.push(event.payload);
        }
        if (event.type === "SUCCESS") {
          if (this.context.buffer.length === 0) {
            this.state = "IDLE";
          } else if (this.context.buffer.length >= this.maxBufferSize) {
            // If buffer refilled to threshold while flushing, send immediately.
            // Otherwise keep buffering and let interval/manual/unload handle it.
            this.state = "BUFFERING";
            void this.flush();
          } else {
            this.state = "BUFFERING";
          }
        }
        if (event.type === "FAIL") {
          this.state = "ERROR";
        }
        break;

      case "ERROR":
        if (event.type === "ADD_LOG") {
          this.context.buffer.push(event.payload);
        }
        if (event.type === "FLUSH") {
          void this.flush(); // retry
        }
        break;
    }
  }

  private async flush() {
    if (this.context.buffer.length === 0) return;

    const toSend = [...this.context.buffer];
    this.inFlight = toSend;
    this.context.buffer = [];
    this.state = "FLUSHING";

    try {
      await this.flushHandler(toSend);
      this.inFlight = [];
      if (this.dropCurrentFlushOutcome) {
        this.dropCurrentFlushOutcome = false;
        return;
      }
      this.dispatch({ type: "SUCCESS" });
    } catch {
      this.inFlight = [];
      if (this.dropCurrentFlushOutcome) {
        this.dropCurrentFlushOutcome = false;
        return;
      }
      this.context.buffer = [...toSend, ...this.context.buffer];
      this.dispatch({ type: "FAIL" });
    }
  }
}

// --------- LOGGER CORE ---------  provides what to do for logger fsm.

export type LoggerOptions = {
  flushInterval?: number;
  maxBufferSize?: number;
  sendLogs: (logs: BaseLogEvent[]) => Promise<void>;
};

export class LoggerCore {
  private fsm: LoggerFSM;
  private flushTimer?: ReturnType<typeof setTimeout>;
  private readonly maxBufferSize: number;
  private readonly flushInterval: number;

  constructor(private options: LoggerOptions) {
    this.maxBufferSize = options?.maxBufferSize ?? DEFAULT_MAX_BUFFER_SIZE;
    this.flushInterval = options?.flushInterval ?? DEFAULT_FLUSH_INTERVAL_MS;
    this.fsm = new LoggerFSM(this.options?.sendLogs, this.maxBufferSize);
  }

  log(event: BaseLogEvent) {
    this.assertValidEvent(event);

    this.fsm.dispatch({ type: "ADD_LOG", payload: event });
    const bufferSize = this.getBufferSize();

    if (bufferSize === 1) {
      this.scheduleFlushTimer();
    }

    if (bufferSize >= this.maxBufferSize && this.getState() !== "FLUSHING") {
      this.flush();
    }
  }

  flush() {
    this.clearFlushTimer();
    if (this.getBufferSize() > 0) {
      this.fsm.dispatch({ type: "FLUSH" });
    }
  }

  getState() {
    return this.fsm.getState();
  }

  /** Returns all pending logs (buffer + in-flight) and clears them for unload persistence. */
  drainBuffer(): BaseLogEvent[] {
    return this.fsm.getPendingAndClear();
  }

  destroy() {
    this.clearFlushTimer();
  }

  private getBufferSize() {
    return this.fsm.getBufferSize();
  }

  private scheduleFlushTimer() {
    if (this.flushInterval <= 0 || this.flushTimer) return;

    this.flushTimer = globalThis.setTimeout(() => {
      this.flushTimer = undefined;
      this.flush();
    }, this.flushInterval);
  }

  private clearFlushTimer() {
    if (!this.flushTimer) return;
    clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
  }

  private assertValidEvent(event: BaseLogEvent) {
    if (!event.title?.trim()) {
      throw new Error("Logger event title is required");
    }
    if (event.category === undefined || event.category === null) {
      throw new Error("Logger event category is required");
    }
    if (event.subCategory === undefined || event.subCategory === null) {
      throw new Error("Logger event subCategory is required");
    }
    if (event.tag === undefined || event.tag === null) {
      throw new Error("Logger event tag is required");
    }
    if (event.type === undefined || event.type === null) {
      throw new Error("Logger event type is required");
    }
    if (!event.context || typeof event.context !== "object") {
      throw new Error("Logger event context must be an object");
    }
  }
}

// export helper
export function createLogger(options: LoggerOptions) {
  return new LoggerCore(options);
}
